import React, { useState, useEffect } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Formik, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import debounce from 'debounce-promise';
import { Form } from 'reactstrap';

import { useStore } from '../../../store';
import useBranches from '../hooks/useBranches';
import { COMPANY_UID_NOT_FOUND, END_USERS_ROLE } from '../../../utils/constants';
import '../../../components/FormComponents/styles.scss';
import CompanyOption from './CompanyOption';
import TextFieldFormGroup from './TextFieldFormGroup';
import SelectFormGroup from './SelectFormGroup';
import CustomSelect from '../../../components/CustomSelect';
import Modal from '../../../components/Modal';
import CompanySelect from '../../../components/CompanySelect';
import _ from 'lodash';
import makeBranchItem from '../helper/makeBranchItem';
import { COMPANY_NOT_FOUND_FIELD, FUNCTION_OPTIONS, SALUTATION_OPTIONS, BRANCH_NOT_FOUND } from '../constants';
import formatOptions from '../helper/formatOptions';
import PrimaryButton from '../../../components/PrimaryButton';
import { getCompanies } from '../services/getCompanies';
import createMainBranch from '../helper/createMainBranch';
import addErrorClassName from '../helper/addErrorClassName';
import Spinner from '../../../components/Spinner';
import { companyFactory } from '../factory/companyFactory';

const ContactDetailsForm = observer(({ handleSubmit, companyData }) => {
  const {
    rootStore: { profileStore },
  } = useStore();
  const { t } = useTranslation();
  const { branches, setBranches, branchesLoaded, handleSetBranches, initialValues } = useBranches(
    toJS(profileStore),
    companyData
  );
  const [flagToRerenderCompanySelect, setFlagToRerenderCompanySelect] = useState(null);
  const [showSwitchingCompanyModal, setShowSwitchingCompanyModal] = useState(false);
  const [modal, setModal] = useState({
    description: '',
    title: '',
  });
  const [initialBranch, setInitialBranch] = useState({ admin: { isManaged: false } });
  const [newCompany, setNewCompany] = useState(null);

  useEffect(() => {
    const checkIfInitialDataIsCompanyNotFoundOrDelete = () => {
      const storeCompany = toJS(profileStore.companyData);
      if (!storeCompany.admin) {
        return companyFactory.getCompanyAdminStatus(false);
      }
      return storeCompany;
    };
    const initialCompanyInfo = checkIfInitialDataIsCompanyNotFoundOrDelete();
    setInitialBranch(initialCompanyInfo);
    // eslint-disable-next-line
  }, []);

  const isEndUser = profileStore.connection === END_USERS_ROLE;

  const initialData = toJS(profileStore.profileData);

  const handleChangeInitialData = () => {
    setInitialBranch(newCompany);
  };

  const handleCloseModal = async setFieldValue => {
    await handleSetBranches(initialBranch);
    setShowSwitchingCompanyModal(false);
    setFlagToRerenderCompanySelect(Math.random());
    setFieldValue('company.branch', initialValues.company.branch);
    setFieldValue('company.name', initialValues.company.name);
    setFieldValue('company.uid', initialValues.company.uid);
    setFieldValue('companyData', initialValues.companyData);
  };
  const handleChangeCompany = (setFieldValue, setFieldTouched) => {
    return async (company, { action }) => {
      if (action === 'clear') {
        clearCompanyValues(setFieldValue);
      }
      changeCompanyValues(company, setFieldValue, setFieldTouched);
    };
  };
  const clearCompanyValues = setFieldValue => {
    setFieldValue('company.uid', null);
    setFieldValue('company.uri', null);
    setFieldValue('company.name', null);
    setBranches([]);
    setNewCompany({ admin: { isManaged: false } });
  };

  const changeCompanyValues = async (company, setFieldValue, setFieldTouched) => {
    const [companyData, isSelectedCompanyManaged] = await loadCompanyData(company?.uid, company?.uri);
    await handleAddModals(company, isSelectedCompanyManaged);
    setFieldTouched('companyData');
    setFieldValue('companyData', company);
    setFieldTouched('companyUID', true, true);
    setFieldValue('company.branch', null);

    const mainAddress = company?.company.mainAddress || {};

    if (company) {
      setFieldValue('company.branch', createMainBranch(mainAddress).value);
    } else {
      setFieldValue('company.branch', null);
      setBranches([]);
      return;
    }

    await handleSetBranches(companyData);
  };

  const handleAddModals = async (company, isSelectedCompanyManaged) => {
    const selectedCompanyManaged =
      company !== null && company.value !== COMPANY_UID_NOT_FOUND && isSelectedCompanyManaged;

    setNewCompany(selectedCompanyManaged !== false ? toJS(profileStore.companyData) : { admin: false });

    if (initialBranch?.admin?.isManaged === true) {
      setShowSwitchingCompanyModal(true);
      setModal({
        description: t('switchingCompanyPopups.fromManaged.description'),
        title: t('switchingCompanyPopups.fromManaged.title'),
      });
    } else if (selectedCompanyManaged === true) {
      setShowSwitchingCompanyModal(true);
      setModal({
        description: t('switchingCompanyPopups.fromUnmanaged.description'),
        title: t('switchingCompanyPopups.fromUnmanaged.title'),
      });
    }
  };

  const loadCompanyData = async (companyUid, companyUri) => {
    const { data } = await profileStore.loadCompanyData(companyUid, companyUri);
    return [data, data?.admin?.isManaged];
  };
  const displayBranchPlaceholder = branches => {
    return branches.length > 1 ? t('address.choosePlaceholder') : t('address.noOptions');
  };

  if (!branchesLoaded || !initialData) {
    return <Spinner pageLoading />;
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        touchOnChange
        validateOnMount={true}
        validate={values => {
          return profileStore.validateProfile(values, t);
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values).then(() => {
            setSubmitting(false);
            handleChangeInitialData();
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => {
          return (
            <Form className="styled-form" onSubmit={handleSubmit}>
              <TextFieldFormGroup
                name="firstName"
                id="signup-first"
                placeholder={t('firstName.placeholder')}
                handleChange={handleChange}
                handleBlur={handleBlur}
                values={values}
                errors={errors}
                touched={touched}
                label={t('firstName.label')}
              />
              <TextFieldFormGroup
                name="lastName"
                id="signup-last"
                placeholder={t('lastName.placeholder')}
                handleChange={handleChange}
                handleBlur={handleBlur}
                values={values}
                errors={errors}
                touched={touched}
                label={t('lastName.label')}
              />
              <SelectFormGroup
                errors={errors}
                touched={touched}
                label={t('salutation.label')}
                name="salutation"
                errorName="salutation"
              >
                <Field
                  className="styled-field"
                  component={CustomSelect}
                  isSearchable={false}
                  name="salutation"
                  options={formatOptions(SALUTATION_OPTIONS, t)}
                  placeholder={t('salutation.placeholder')}
                />
              </SelectFormGroup>

              {!isEndUser && (
                <>
                  <SelectFormGroup
                    errors={errors}
                    touched={touched}
                    label={t('company.label')}
                    name="companyData"
                    errorName="company"
                  >
                    <Modal
                      isOpen={showSwitchingCompanyModal}
                      title={modal.title}
                      description={modal.description}
                      placeholder={t('selectSuccessorModal.switchCompany.modalTitle')}
                      onClose={() => handleCloseModal(setFieldValue)}
                      onConfirm={() => setShowSwitchingCompanyModal(false)}
                    />
                    <CompanySelect
                      key={flagToRerenderCompanySelect}
                      name="companyData"
                      placeholder={t('company.placeholder')}
                      noOptionMessage={t('company.noResults')}
                      defaultValue={initialValues.companyData}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      loadOptions={debounce(getCompanies, 500)}
                      value={values.companyData}
                      components={{
                        Option: CompanyOption,
                      }}
                      getOptionLabel={option =>
                        option.label === COMPANY_NOT_FOUND_FIELD.label ? t(option.label) : option.label
                      }
                      onBlur={() => {
                        setFieldTouched('companyData');
                      }}
                      onChange={handleChangeCompany(setFieldValue, setFieldTouched)}
                      className={addErrorClassName(errors, touched, 'companyData')}
                    />
                  </SelectFormGroup>

                  {values.companyData && values.companyData.value !== 'uid-not-found' && (
                    <SelectFormGroup
                      errors={errors}
                      touched={touched}
                      label={t('address.label')}
                      name="language"
                      errorName="company.branch"
                    >
                      <Field
                        className="styled-field"
                        component={CustomSelect}
                        key={`address-field-${values.company.address}`}
                        name="company.branch"
                        isSearchable={true}
                        isClearable={true}
                        options={branches}
                        value={values.company.branch}
                        getOptionLabel={option =>
                          option && option.value === BRANCH_NOT_FOUND
                            ? t(`${_.get(option, 'label')}`)
                            : `${_.get(option, 'label')}`
                        }
                        placeholder={displayBranchPlaceholder(branches)}
                        onChange={branch => {
                          setFieldTouched('company.branch');
                          setFieldValue('company.branch', branch ? makeBranchItem(branch.value) : null);
                        }}
                      />
                    </SelectFormGroup>
                  )}
                  <SelectFormGroup
                    errors={errors}
                    touched={touched}
                    label={t('function.label')}
                    name="userFunction"
                    errorName="userFunction"
                  >
                    <Field
                      className="styled-field"
                      component={CustomSelect}
                      isSearchable={false}
                      name="userFunction"
                      options={formatOptions(FUNCTION_OPTIONS, t)}
                      placeholder={t('function.placeholder')}
                    />
                  </SelectFormGroup>
                </>
              )}
              <PrimaryButton
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length !== 0 || Object.keys(touched).length === 0}
              >
                {t('saveButton')}
              </PrimaryButton>
            </Form>
          );
        }}
      </Formik>
    </>
  );
});

export default ContactDetailsForm;
